import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Alert, Skeleton, Button } from '@mui/material';
import { TrendingUp, MonetizationOn, Assessment, ArrowForward } from '@mui/icons-material';
import { useQuickRecommendation } from '../hooks/usePricing';
import { useNavigate } from 'react-router-dom';

const QuickPricingRecommendation: React.FC = () => {
  const navigate = useNavigate();
  const { data: recommendation, isLoading, error } = useQuickRecommendation();

  if (isLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Quick Pricing Recommendation
          </Typography>
          <Skeleton variant="rectangular" height={80} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={80} />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Quick Pricing Recommendation
          </Typography>
          <Alert severity="error">Failed to load pricing recommendation</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!recommendation) {
    return null;
  }

  const marginColor =
    recommendation.margin_at_recommended >= 25
      ? '#4caf50'
      : recommendation.margin_at_recommended >= 15
        ? '#ff9800'
        : '#f44336';

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Quick Pricing Recommendation
          </Typography>
          <TrendingUp sx={{ color: '#ff9800' }} />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Cost Per Crate
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment sx={{ color: '#ff9800', fontSize: 20 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              GH₵ {recommendation.cost_per_crate?.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: 'rgba(76, 175, 80, 0.1)',
            border: '2px solid #4caf50',
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Recommended Price
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MonetizationOn sx={{ color: '#4caf50', fontSize: 24 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
              GH₵ {recommendation.recommended_price?.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${recommendation.margin_at_recommended}% Margin`}
              size="small"
              sx={{
                bgcolor: marginColor,
                color: '#000',
                fontWeight: 600,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Profit: GH₵{' '}
              {(recommendation.recommended_price - recommendation.cost_per_crate).toFixed(2)}
              /crate
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Minimum Viable Price
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            GH₵ {recommendation.minimum_price?.toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Break-even with minimal profit
          </Typography>
        </Box>

        {recommendation.margin_at_recommended < 20 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Current margin is below recommended 20%. Consider price adjustment.
          </Alert>
        )}

        <Button
          fullWidth
          variant="outlined"
          endIcon={<ArrowForward />}
          onClick={() => navigate('/pricing-suggestions')}
          sx={{ mt: 1 }}
        >
          View Detailed Analysis
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: 'block', textAlign: 'center' }}
        >
          Based on last 30 days • Updated:{' '}
          {new Date(recommendation.last_updated).toLocaleDateString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default QuickPricingRecommendation;
